import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import lodash from "lodash";

import type { RootState } from "@/state/types";

export type Country = {
  alpha2: string;
  atium_id: string;
  name: string;
  region: string;
};

export type Subcountry = {
  type: string;
  name: string;
  iso_code: string;
  atium_subcountry_id: string;
  atium_country_id: string;
};

export type Industry = {
  name: string;
  atium_id: string;
  type: string;
};

export type Category = {
  atium_id: string;
  name: string;
  type: string;
  id: number;
};

export type CountryDictionary = Record<string, Country>;
export type SubcountryDictionary = Record<string, Subcountry[]>;
export type IndustryDictionary = Record<string, Industry>;
export type CategoryDictionary = Record<number, Category>;

export type ClassificationPayload = {
  countries: CountryDictionary;
  subcountries: SubcountryDictionary;
  industries: IndustryDictionary;
  categories: CategoryDictionary;
  /** The org context this payload was fetched for, or null if no org context applies. */
  organizationId: string | null;
};

/**
 * What setClassification takes: each list that loaded. A list left out (its document failed to
 * load) keeps what the store had, so one failed read does not empty the others.
 */
export type ClassificationUpdate = Partial<
  Omit<ClassificationPayload, "organizationId">
> &
  Pick<ClassificationPayload, "organizationId">;

type ClassificationState = ClassificationPayload & {
  /** Tracks which org's data is currently in the store. null = no org context. */
  loadedForOrganizationId: string | null;
  /** Distinguishes "not yet loaded" from "loaded with no org context" (both org values are null). */
  hasLoaded: boolean;
};

const initialState: ClassificationState = {
  countries: {},
  subcountries: {},
  industries: {},
  categories: {},
  organizationId: null,
  loadedForOrganizationId: null,
  hasLoaded: false,
};

export const classificationSlice = createSlice({
  name: "classification",
  initialState,
  reducers: {
    setClassification: (
      state,
      { payload }: PayloadAction<ClassificationUpdate>
    ) => {
      if (payload.countries) state.countries = payload.countries;
      if (payload.subcountries) state.subcountries = payload.subcountries;
      if (payload.industries) state.industries = payload.industries;
      if (payload.categories) state.categories = payload.categories;
      state.loadedForOrganizationId = payload.organizationId;
      state.hasLoaded = true;
    },
  },
});

export const { setClassification } = classificationSlice.actions;

export const selectCountries = (state: RootState): CountryDictionary =>
  state.classification.countries;
export const selectSubcountries = (state: RootState): SubcountryDictionary =>
  state.classification.subcountries;
export const selectIndustries = (state: RootState): IndustryDictionary =>
  state.classification.industries;
export const selectCategories = (state: RootState): CategoryDictionary =>
  state.classification.categories;
/** The org ID whose master data is currently loaded, or null if no org context. */
export const selectLoadedOrganizationId = (state: RootState): string | null =>
  state.classification.loadedForOrganizationId;
/** Whether classification data has been fetched at least once. */
export const selectHasLoadedClassification = (state: RootState): boolean =>
  state.classification.hasLoaded;

export type CountryWithSubcountries = Country & {
  type: "country";
  iso_code: string;
  subcountries: SubcountryWithCountry[];
};

export type SubcountryWithCountry = Subcountry & {
  type: "subcountry";
  country: Country & { iso_code: string };
};

export type CountryOrSubcountry =
  | CountryWithSubcountries
  | SubcountryWithCountry;

export type CountriesAndSubcountries = {
  array: string[];
  map: Record<string, CountryOrSubcountry>;
};

export const selectCountriesAndSubcountries = createSelector(
  selectCountries,
  selectSubcountries,
  (countries, subcountriesByCountryCode): CountriesAndSubcountries => {
    const countryDictionary = lodash(countries)
      .mapValues<CountryWithSubcountries>((countryData, countryCode) => ({
        ...countryData,
        type: "country",
        iso_code: countryCode,
        subcountries: lodash(subcountriesByCountryCode[countryCode] ?? [])
          .sortBy("name")
          .map<SubcountryWithCountry>((subcountryData) => ({
            ...subcountryData,
            type: "subcountry",
            country: { ...countryData, iso_code: countryCode },
          }))
          .value(),
      }))
      .value();

    const countriesAndSubcountries = lodash(countryDictionary)
      .sortBy("name")
      .flatMap<CountryOrSubcountry>((countryData) => [
        countryData,
        ...countryData.subcountries,
      ]);

    const countryAndSubcountryIsoCodes = countriesAndSubcountries
      .map("iso_code")
      .uniq()
      .value();

    const countryAndSubcountryDictionary = countriesAndSubcountries
      .keyBy("iso_code")
      .value();

    return {
      array: countryAndSubcountryIsoCodes,
      map: countryAndSubcountryDictionary,
    };
  }
);

export type SubcountryWithCountryName = Subcountry & {
  country: string;
};

export const selectCountryBySubcountry = createSelector(
  selectCountries,
  selectSubcountries,
  (countries, subcountries): Record<string, SubcountryWithCountryName> => {
    const newSubcountryMapping: Record<string, SubcountryWithCountryName> = {};
    for (const [countryCode, subcountryList] of Object.entries(subcountries)) {
      for (const subcon of subcountryList) {
        newSubcountryMapping[subcon.iso_code] = {
          ...subcon,
          name: `${countries[countryCode]?.name ?? countryCode} - ${
            subcon.name
          }`,
          country: countryCode,
        };
      }
    }
    return newSubcountryMapping;
  }
);

export default classificationSlice.reducer;
